import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './BoardOrderContextMenu.css';

const DEFAULT_SUBMENU_GAP = 4;
const VIEWPORT_MARGIN = 8;
const SUBMENU_CLOSE_DELAY_MS = 150;

/**
 * Menu de contexto para ações sobre ordem de serviço no Board
 * (coluna "Em aberto" e colunas de técnicos).
 * Itens podem ter subItems para submenu em hover (ex.: Alterar técnico → técnicos → seções).
 * @param {{ visible: boolean, x: number, y: number, menuZIndex?: number, submenuGap?: number, submenuCloseDelayMs?: number, submenuPreferLeft?: boolean, submenuOpenOnClick?: boolean, initialOpenSubmenuId?: string | null, portalRoot?: Element | null, menuClassName?: string, items: Array<{ id?: string, type?: 'item'|'separator'|'label', label?: string, disabled?: boolean, variant?: 'default'|'destructive', shortcut?: string, onClick?: function, icon?: string, iconNode?: import('react').ReactNode, subItems?: Array }>, onClose: function }} props
 */
export function BoardOrderContextMenu({
  visible,
  x,
  y,
  items,
  onClose,
  menuZIndex = 10000,
  submenuGap = DEFAULT_SUBMENU_GAP,
  submenuCloseDelayMs = SUBMENU_CLOSE_DELAY_MS,
  submenuPreferLeft = false,
  submenuOpenOnClick = false,
  menuClassName = '',
  initialOpenSubmenuId = null,
  portalRoot = null,
}) {
  const [openSubmenuId, setOpenSubmenuId] = useState(null);
  const [openNestedId, setOpenNestedId] = useState(null);
  const [openDeepId, setOpenDeepId] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const [nestedPosition, setNestedPosition] = useState({ top: 0, left: 0 });
  const [deepPosition, setDeepPosition] = useState({ top: 0, left: 0 });
  const rowRefs = useRef({});
  const rootMenuRef = useRef(null);
  const submenuRef = useRef(null);
  const nestedRef = useRef(null);
  const deepRef = useRef(null);
  const closeSubmenuTimeoutRef = useRef(null);
  const closeNestedTimeoutRef = useRef(null);
  const closeDeepTimeoutRef = useRef(null);
  const didAutoOpenSubmenuRef = useRef(false);
  const [rootPos, setRootPos] = useState({ left: x, top: y });

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!visible) {
      didAutoOpenSubmenuRef.current = false;
      setOpenSubmenuId(null);
      setOpenNestedId(null);
      setOpenDeepId(null);
      if (closeSubmenuTimeoutRef.current) clearTimeout(closeSubmenuTimeoutRef.current);
      if (closeNestedTimeoutRef.current) clearTimeout(closeNestedTimeoutRef.current);
      if (closeDeepTimeoutRef.current) clearTimeout(closeDeepTimeoutRef.current);
    }
  }, [visible]);

  /** Fecha com qualquer ação fora (clique, scroll, Escape, resize) — não fica “preso” no Board. */
  useEffect(() => {
    if (!visible) return undefined;

    const isInsideMenu = (target) => {
      let el = target;
      if (el && el.nodeType === Node.TEXT_NODE) el = el.parentElement;
      if (!el || typeof el.closest !== 'function') return false;
      return Boolean(el.closest('.context-menu') || el.closest('.board-order-context-menu'));
    };

    const dismiss = () => onCloseRef.current?.();

    const onPointerDown = (e) => {
      if (isInsideMenu(e.target)) return;
      dismiss();
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
      }
    };

    const onScroll = (e) => {
      if (isInsideMenu(e.target)) return;
      dismiss();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [visible]);

  const scheduleCloseSubmenu = () => {
    if (closeSubmenuTimeoutRef.current) clearTimeout(closeSubmenuTimeoutRef.current);
    closeSubmenuTimeoutRef.current = setTimeout(() => setOpenSubmenuId(null), submenuCloseDelayMs);
  };

  const cancelCloseSubmenu = () => {
    if (closeSubmenuTimeoutRef.current) {
      clearTimeout(closeSubmenuTimeoutRef.current);
      closeSubmenuTimeoutRef.current = null;
    }
  };

  const scheduleCloseNested = () => {
    if (closeNestedTimeoutRef.current) clearTimeout(closeNestedTimeoutRef.current);
    closeNestedTimeoutRef.current = setTimeout(() => setOpenNestedId(null), submenuCloseDelayMs);
  };

  const cancelCloseNested = () => {
    if (closeNestedTimeoutRef.current) {
      clearTimeout(closeNestedTimeoutRef.current);
      closeNestedTimeoutRef.current = null;
    }
  };

  const scheduleCloseDeep = () => {
    if (closeDeepTimeoutRef.current) clearTimeout(closeDeepTimeoutRef.current);
    closeDeepTimeoutRef.current = setTimeout(() => setOpenDeepId(null), submenuCloseDelayMs);
  };

  const cancelCloseDeep = () => {
    if (closeDeepTimeoutRef.current) {
      clearTimeout(closeDeepTimeoutRef.current);
      closeDeepTimeoutRef.current = null;
    }
  };

  const updateSubmenuPosition = (rowEl) => {
    if (!rowEl) return;
    const rect = rowEl.getBoundingClientRect();
    setSubmenuPosition({
      top: rect.top,
      left: submenuPreferLeft ? rect.left - submenuGap : rect.right + submenuGap,
    });
  };

  const updateNestedPosition = (rowEl) => {
    if (!rowEl) return;
    const rect = rowEl.getBoundingClientRect();
    setNestedPosition({
      top: rect.top,
      left: submenuPreferLeft ? rect.left - submenuGap : rect.right + submenuGap,
    });
  };

  const updateDeepPosition = (rowEl) => {
    if (!rowEl) return;
    const rect = rowEl.getBoundingClientRect();
    setDeepPosition({
      top: rect.top,
      left: submenuPreferLeft ? rect.left - submenuGap : rect.right + submenuGap,
    });
  };

  useLayoutEffect(() => {
    if (!openSubmenuId) return;
    const trigger = rowRefs.current[openSubmenuId];
    const sub = submenuRef.current;
    if (!trigger || !sub) return;
    const tr = trigger.getBoundingClientRect();
    const sr = sub.getBoundingClientRect();
    let left = submenuPreferLeft ? tr.left - sr.width - submenuGap : sr.left;
    let top = sr.top;
    if (!submenuPreferLeft && sr.right > window.innerWidth - VIEWPORT_MARGIN) {
      left = tr.left - sr.width - submenuGap;
    }
    if (submenuPreferLeft && sr.left < VIEWPORT_MARGIN) {
      left = tr.right + submenuGap;
    }
    if (sr.left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (sr.bottom > window.innerHeight - VIEWPORT_MARGIN) top = window.innerHeight - sr.height - VIEWPORT_MARGIN;
    if (sr.top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;
    setSubmenuPosition({ left, top });
  }, [openSubmenuId, submenuGap, submenuPreferLeft]);

  useLayoutEffect(() => {
    if (!openNestedId || !nestedRef.current) return;
    const nestedTriggerEl = submenuRef.current?.querySelector(`[data-nested-trigger="${openNestedId}"]`);
    const nr = nestedRef.current.getBoundingClientRect();
    let left = nr.left;
    let top = nr.top;
    if (submenuPreferLeft) {
      if (nestedTriggerEl) {
        const tr = nestedTriggerEl.getBoundingClientRect();
        left = tr.left - nr.width - submenuGap;
      }
    } else if (nr.right > window.innerWidth - VIEWPORT_MARGIN && nestedTriggerEl) {
      const tr = nestedTriggerEl.getBoundingClientRect();
      left = tr.left - nr.width - submenuGap;
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (nr.bottom > window.innerHeight - VIEWPORT_MARGIN) top = window.innerHeight - nr.height - VIEWPORT_MARGIN;
    if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;
    setNestedPosition({ left, top });
  }, [openNestedId, submenuGap, submenuPreferLeft]);

  useLayoutEffect(() => {
    if (!openDeepId || !deepRef.current) return;
    const deepTriggerEl = nestedRef.current?.querySelector(`[data-deep-trigger="${openDeepId}"]`);
    const dr = deepRef.current.getBoundingClientRect();
    let left = dr.left;
    let top = dr.top;
    if (submenuPreferLeft) {
      if (deepTriggerEl) {
        const tr = deepTriggerEl.getBoundingClientRect();
        left = tr.left - dr.width - submenuGap;
      }
    } else if (dr.right > window.innerWidth - VIEWPORT_MARGIN && deepTriggerEl) {
      const tr = deepTriggerEl.getBoundingClientRect();
      left = tr.left - dr.width - submenuGap;
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (dr.bottom > window.innerHeight - VIEWPORT_MARGIN) top = window.innerHeight - dr.height - VIEWPORT_MARGIN;
    if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;
    setDeepPosition({ left, top });
  }, [openDeepId, submenuGap, submenuPreferLeft]);

  useLayoutEffect(() => {
    if (!visible || !initialOpenSubmenuId || !items?.length || didAutoOpenSubmenuRef.current) return;
    const entry = items.find((i) => String(i.id) === String(initialOpenSubmenuId));
    if (!entry?.subItems?.length || entry.disabled) return;
    didAutoOpenSubmenuRef.current = true;
    cancelCloseSubmenu();
    setOpenSubmenuId(initialOpenSubmenuId);
    setOpenNestedId(null);
    setOpenDeepId(null);
    const syncPos = () => {
      const row = rowRefs.current[initialOpenSubmenuId];
      if (row) {
        const rect = row.getBoundingClientRect();
        setSubmenuPosition({
          top: rect.top,
          left: submenuPreferLeft ? rect.left - submenuGap : rect.right + submenuGap,
        });
      }
    };
    syncPos();
    requestAnimationFrame(syncPos);
  }, [visible, initialOpenSubmenuId, items, submenuGap, submenuPreferLeft, x, y]);

  /** Mantém o menu raiz inteiro na viewport (ex.: clique no fim da página).
   * Só reage a abertura/posição do clique — re-renders (tooltip etc.) não devem
   * forçar o menu de volta ao y cru (e cortar na borda). */
  useLayoutEffect(() => {
    if (!visible) return;

    const clampRoot = () => {
      const el = rootMenuRef.current;
      if (!el) {
        setRootPos({ left: x, top: y });
        return;
      }
      const width = el.offsetWidth || el.getBoundingClientRect().width || 180;
      const height = el.offsetHeight || el.getBoundingClientRect().height || 80;
      const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
      const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);
      let left = x;
      let top = y;
      if (left > maxLeft) left = maxLeft;
      if (top > maxTop) top = maxTop;
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
      if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;
      setRootPos((prev) =>
        prev.left === left && prev.top === top ? prev : { left, top }
      );
    };

    clampRoot();
    // Segunda medição após paint (fontes/ícones podem alterar altura)
    const raf = requestAnimationFrame(clampRoot);
    return () => cancelAnimationFrame(raf);
  }, [visible, x, y]);

  if (!visible || !items?.length) return null;

  const zFly1 = menuZIndex + 10;
  const zFly2 = menuZIndex + 20;
  const zFly3 = menuZIndex + 30;

  const rootMenuClass = ['context-menu', 'board-order-context-menu', menuClassName]
    .filter(Boolean)
    .join(' ');
  const flyMenuClass = ['context-menu', 'board-order-context-menu', 'board-order-context-menu-submenu', menuClassName]
    .filter(Boolean)
    .join(' ');

  const itemClass = (opts = {}) =>
    [
      'board-order-context-menu-item',
      opts.disabled ? 'is-disabled' : '',
      opts.hasSub ? 'has-submenu' : '',
      opts.open ? 'is-open' : '',
      opts.destructive ? 'is-destructive' : '',
    ]
      .filter(Boolean)
      .join(' ');

  const isSeparator = (item) => item?.type === 'separator';
  const isLabel = (item) => item?.type === 'label';
  const isDestructive = (item) => item?.variant === 'destructive';

  const renderItemLabel = (item) => (
    <span className="board-order-context-menu-item-label">
      {item.iconNode ? (
        <span className="board-order-context-menu-item-icon">{item.iconNode}</span>
      ) : item.icon ? (
        <i className={`${item.icon} board-order-context-menu-item-icon`} aria-hidden />
      ) : null}
      <span>{item.label}</span>
    </span>
  );

  const renderLeafContent = (item) => (
    <>
      {renderItemLabel(item)}
      {item.shortcut ? (
        <span className="board-order-context-menu-shortcut">{item.shortcut}</span>
      ) : null}
    </>
  );

  const menuTree = (
    <div
      ref={rootMenuRef}
      className={rootMenuClass}
      role="menu"
      style={{
        position: 'fixed',
        left: `${rootPos.left}px`,
        top: `${rootPos.top}px`,
        zIndex: menuZIndex,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, itemIdx) => {
        if (isSeparator(item)) {
          return (
            <div
              key={item.id || `sep-${itemIdx}`}
              className="board-order-context-menu-separator"
              role="separator"
            />
          );
        }
        if (isLabel(item)) {
          return (
            <div key={item.id || `label-${itemIdx}`} className="board-order-context-menu-label">
              {item.label}
            </div>
          );
        }

        const hasSubItems = item.subItems?.length > 0;
        const isOpen = openSubmenuId === item.id;
        const openSub = hasSubItems ? item.subItems.find((s) => s.id === openNestedId) : null;
        const destructive = isDestructive(item);

        return (
          <React.Fragment key={item.id || `item-${itemIdx}`}>
            <div
              ref={(el) => { rowRefs.current[item.id] = el; }}
              role="menuitem"
              aria-disabled={item.disabled || undefined}
              aria-haspopup={hasSubItems ? 'menu' : undefined}
              aria-expanded={hasSubItems ? isOpen : undefined}
              className={itemClass({
                disabled: item.disabled,
                hasSub: hasSubItems,
                open: isOpen,
                destructive,
              })}
              onMouseEnter={(e) => {
                if (item.disabled) {
                  if (hasSubItems) {
                    setOpenSubmenuId(null);
                    setOpenNestedId(null);
                    setOpenDeepId(null);
                  }
                  return;
                }
                if (hasSubItems && !submenuOpenOnClick) {
                  cancelCloseSubmenu();
                  setOpenSubmenuId(item.id);
                  setOpenNestedId(null);
                  updateSubmenuPosition(e.currentTarget);
                }
              }}
              onMouseLeave={() => {
                if (hasSubItems && !item.disabled && !submenuOpenOnClick) scheduleCloseSubmenu();
              }}
              onClick={(e) => {
                if (item.disabled) return;
                if (hasSubItems && submenuOpenOnClick) {
                  e.stopPropagation();
                  cancelCloseSubmenu();
                  if (openSubmenuId === item.id) {
                    setOpenSubmenuId(null);
                    setOpenNestedId(null);
                    setOpenDeepId(null);
                  } else {
                    setOpenSubmenuId(item.id);
                    setOpenNestedId(null);
                    setOpenDeepId(null);
                    updateSubmenuPosition(e.currentTarget);
                  }
                  return;
                }
                if (!hasSubItems && item.onClick) {
                  item.onClick();
                  onClose?.();
                }
              }}
            >
              {renderLeafContent(item)}
              {hasSubItems && (
                <i
                  className={`bi ${submenuPreferLeft ? 'bi-chevron-left' : 'bi-chevron-right'} board-order-context-menu-item-chevron`}
                  aria-hidden
                />
              )}
            </div>
            {hasSubItems && !item.disabled && isOpen && (
              <>
                <div
                  ref={submenuRef}
                  role="menu"
                  className={flyMenuClass}
                  style={{ position: 'fixed', zIndex: zFly1, left: submenuPosition.left, top: submenuPosition.top }}
                  onMouseEnter={cancelCloseSubmenu}
                  onMouseLeave={() => scheduleCloseSubmenu()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.subItems.map((sub, subIdx) => {
                    if (isSeparator(sub)) {
                      return (
                        <div
                          key={sub.id || `sub-sep-${subIdx}`}
                          className="board-order-context-menu-separator"
                          role="separator"
                        />
                      );
                    }
                    if (isLabel(sub)) {
                      return (
                        <div key={sub.id || `sub-label-${subIdx}`} className="board-order-context-menu-label">
                          {sub.label}
                        </div>
                      );
                    }
                    const hasNested = sub.subItems?.length > 0;
                    const isNestedOpen = openNestedId === sub.id;
                    return (
                      <React.Fragment key={sub.id || `sub-${subIdx}`}>
                        <div
                          data-nested-trigger={sub.id}
                          role="menuitem"
                          aria-haspopup={hasNested ? 'menu' : undefined}
                          aria-expanded={hasNested ? isNestedOpen : undefined}
                          className={itemClass({
                            hasSub: hasNested,
                            open: isNestedOpen,
                            destructive: isDestructive(sub),
                            disabled: sub.disabled,
                          })}
                          onMouseEnter={(e) => {
                            cancelCloseNested();
                            cancelCloseDeep();
                            if (hasNested) {
                              setOpenNestedId(sub.id);
                              updateNestedPosition(e.currentTarget);
                            }
                          }}
                          onMouseLeave={() => {
                            if (hasNested) scheduleCloseNested();
                          }}
                          onClick={() => {
                            if (sub.disabled) return;
                            if (!hasNested && sub.onClick) {
                              sub.onClick();
                              onClose?.();
                            }
                          }}
                        >
                          {renderLeafContent(sub)}
                          {hasNested && (
                            <i className="bi bi-chevron-right board-order-context-menu-item-chevron" aria-hidden />
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                {openSub?.subItems?.length > 0 && (
                  <div
                    ref={nestedRef}
                    role="menu"
                    className={flyMenuClass}
                    style={{ position: 'fixed', zIndex: zFly2, left: nestedPosition.left, top: nestedPosition.top }}
                    onMouseEnter={() => {
                      cancelCloseSubmenu();
                      cancelCloseNested();
                      cancelCloseDeep();
                    }}
                    onMouseLeave={() => {
                      scheduleCloseNested();
                      scheduleCloseDeep();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {openSub.subItems.map((leaf, leafIdx) => {
                      if (isSeparator(leaf)) {
                        return (
                          <div
                            key={leaf.id || `leaf-sep-${leafIdx}`}
                            className="board-order-context-menu-separator"
                            role="separator"
                          />
                        );
                      }
                      if (isLabel(leaf)) {
                        return (
                          <div key={leaf.id || `leaf-label-${leafIdx}`} className="board-order-context-menu-label">
                            {leaf.label}
                          </div>
                        );
                      }
                      const hasDeep = leaf.subItems?.length > 0;
                      const isDeepOpen = openDeepId === leaf.id;
                      return (
                        <React.Fragment key={leaf.id || `leaf-${leafIdx}`}>
                          <div
                            data-deep-trigger={leaf.id}
                            role="menuitem"
                            aria-haspopup={hasDeep ? 'menu' : undefined}
                            aria-expanded={hasDeep ? isDeepOpen : undefined}
                            className={itemClass({
                              hasSub: hasDeep,
                              open: isDeepOpen,
                              destructive: isDestructive(leaf),
                              disabled: leaf.disabled,
                            })}
                            onMouseEnter={(e) => {
                              cancelCloseDeep();
                              if (hasDeep) {
                                setOpenDeepId(leaf.id);
                                updateDeepPosition(e.currentTarget);
                              }
                            }}
                            onMouseLeave={() => {
                              if (hasDeep) scheduleCloseDeep();
                            }}
                            onClick={() => {
                              if (leaf.disabled) return;
                              if (!hasDeep && leaf.onClick) {
                                leaf.onClick();
                                onClose?.();
                              }
                            }}
                          >
                            {renderLeafContent(leaf)}
                            {hasDeep && (
                              <i className="bi bi-chevron-right board-order-context-menu-item-chevron" aria-hidden />
                            )}
                          </div>
                          {hasDeep && isDeepOpen && (
                            <div
                              ref={deepRef}
                              role="menu"
                              className={flyMenuClass}
                              style={{ position: 'fixed', zIndex: zFly3, left: deepPosition.left, top: deepPosition.top }}
                              onMouseEnter={() => {
                                cancelCloseSubmenu();
                                cancelCloseNested();
                                cancelCloseDeep();
                              }}
                              onMouseLeave={() => scheduleCloseDeep()}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {leaf.subItems.map((deepItem, deepIdx) => {
                                if (isSeparator(deepItem)) {
                                  return (
                                    <div
                                      key={deepItem.id || `deep-sep-${deepIdx}`}
                                      className="board-order-context-menu-separator"
                                      role="separator"
                                    />
                                  );
                                }
                                if (isLabel(deepItem)) {
                                  return (
                                    <div
                                      key={deepItem.id || `deep-label-${deepIdx}`}
                                      className="board-order-context-menu-label"
                                    >
                                      {deepItem.label}
                                    </div>
                                  );
                                }
                                return (
                                  <div
                                    key={deepItem.id || `deep-${deepIdx}`}
                                    role="menuitem"
                                    className={itemClass({
                                      destructive: isDestructive(deepItem),
                                      disabled: deepItem.disabled,
                                    })}
                                    onClick={() => {
                                      if (deepItem.disabled) return;
                                      if (deepItem.onClick) {
                                        deepItem.onClick();
                                        onClose?.();
                                      }
                                    }}
                                  >
                                    {renderLeafContent(deepItem)}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  if (portalRoot) {
    return createPortal(menuTree, portalRoot);
  }
  return menuTree;
}

export default BoardOrderContextMenu;
